import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CaretDown, CaretRight, ArrowDown, Plus } from "@phosphor-icons/react";
import { API, Loading, ErrorBox, PriorityChip, IntegrityNote, ACTION_STATUS_META } from "./entryShared";

export function useRegister(caseId) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  useEffect(() => {
    let live = true;
    axios.get(`${API}/access-cases/${caseId}/register`)
      .then((r) => live && setData(r.data))
      .catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [caseId, tick]);
  return { data, error, reload };
}

const effectsToast = (effects) => {
  if (!effects) return;
  if (effects.reverted) {
    toast.info(`Action reopened — effects reversed. Readiness ${effects.readiness_before} → ${effects.readiness_after}`);
    return;
  }
  const parts = [];
  if (effects.evidence_created) parts.push(`Evidence added: ${effects.evidence_created}`);
  if (effects.gaps_closed?.length) parts.push(`Gaps closed: ${effects.gaps_closed.join(", ")}`);
  if (effects.inputs_collected?.length) parts.push(`${effects.inputs_collected.length} localization input(s) collected`);
  const title = effects.readiness_after !== effects.readiness_before
    ? `Readiness ${effects.readiness_before} → ${effects.readiness_after}` : "Action completed";
  toast.success(title, {
    description: parts.join(" · ") || "No readiness change — this action declared no evidence or linked effects.", duration: 7000 });
};

const StatusSelect = ({ action, caseId, onDone }) => {
  const [busy, setBusy] = useState(false);
  const change = (status) => {
    setBusy(true);
    axios.patch(`${API}/access-cases/${caseId}/actions/${action.id}`, { status })
      .then((r) => { effectsToast(r.data.effects); onDone(); })
      .catch((e) => toast.error(e.response?.data?.detail || e.message))
      .finally(() => setBusy(false));
  };
  const meta = ACTION_STATUS_META[action.status] || {};
  return (
    <select data-testid={`status-select-${action.id}`} disabled={busy} value={action.status}
      onChange={(e) => change(e.target.value)}
      className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wide border-0 cursor-pointer ${meta.cls || "bg-slate-200"}`}>
      {Object.entries(ACTION_STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
    </select>
  );
};

const Field = ({ label, children, span }) => (
  <div className={span ? "sm:col-span-2" : ""}>
    <span className="uppercase tracking-widest text-slate-400 text-[9px]">{label}</span>
    <p className="text-slate-700 text-[11px]">{children || "—"}</p>
  </div>
);

const ActionRow = ({ a, caseId, reload }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`px-4 py-3 ${a.critical ? "border-l-2 border-l-amber-500" : ""}`} data-testid={`activity-${a.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 text-left" data-testid={`expand-${a.id}`}>
          {open ? <CaretDown size={12} className="text-slate-400 shrink-0" /> : <CaretRight size={12} className="text-slate-400 shrink-0" />}
          <p className="text-xs font-bold text-slate-800">{a.activity}</p>
        </button>
        <div className="flex items-center gap-2">
          {a.critical && <span className="text-[9px] font-bold uppercase text-amber-600">Critical path</span>}
          {a.approval_status === "pending" && <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase bg-amber-100 text-amber-700">Approval pending</span>}
          {a.approval_status === "approved" && <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase bg-emerald-100 text-emerald-700">Approved</span>}
          <PriorityChip value={a.priority} />
          <StatusSelect action={a} caseId={caseId} onDone={reload} />
        </div>
      </div>
      {a.readiness_delta && a.readiness_delta.before !== a.readiness_delta.after && (
        <p className="text-[10px] text-emerald-700 mt-1 ml-5" data-testid={`readiness-delta-${a.id}`}>
          Completed — readiness moved {a.readiness_delta.before} → {a.readiness_delta.after}</p>
      )}
      {open && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2 mt-3 ml-5 text-[10px]" data-testid={`detail-${a.id}`}>
          <Field label="Strategic rationale" span>{a.strategic_rationale}</Field>
          <Field label="Evidence gap addressed" span>{a.evidence_gap_addressed}</Field>
          <Field label="Access barrier addressed" span>{a.access_barrier_addressed}</Field>
          <Field label="Country">{a.country}</Field>
          <Field label="Owner">{a.owner}</Field>
          <Field label="Client owner">{a.client_role}</Field>
          <Field label="Consultant owner">{a.consultant_role}</Field>
          <Field label="Collaborators" span>{(a.collaborators || []).join(", ")}</Field>
          <Field label="Inputs required" span>{a.input_required}</Field>
          <Field label="Deliverable" span>{a.deliverable || a.output}</Field>
          <Field label="Dependency" span>{a.depends_on?.length ? a.depends_on.join(", ") : (a.dependency || "None")}</Field>
          <Field label="Est. duration">{a.duration_months ?? "—"} mo ({a.duration})</Field>
          <Field label="Target date">{a.target_date}</Field>
          <Field label="Schedule">M{a.start_month}–M{a.end_month}</Field>
          <Field label="Approval status">{(a.approval_status || "not_required").replaceAll("_", " ")}</Field>
          <Field label="Evidence generated on completion" span>
            {a.evidence_generated ? `${a.evidence_generated.name} (${a.evidence_generated.type})` : "None declared"}</Field>
          <Field label="Impact on readiness" span>{(a.readiness_impact || []).join(", ") || "—"}</Field>
        </div>
      )}
    </div>
  );
};

const NewActionDialog = ({ caseId, reload }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ activity: "", strategic_rationale: "", deliverable: "", priority: "medium", owner: "Consultant", duration_months: 1, target_date: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = () => {
    axios.post(`${API}/access-cases/${caseId}/actions`, f)
      .then(() => { toast.success("Action added to the register"); setOpen(false); setF({ ...f, activity: "", strategic_rationale: "", deliverable: "" }); reload(); })
      .catch((e) => toast.error(e.response?.data?.detail || e.message));
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-action-btn" size="sm" className="bg-slate-900 hover:bg-slate-700 text-white rounded-none text-xs h-8">
          <Plus size={13} className="mr-1" /> New Action</Button>
      </DialogTrigger>
      <DialogContent className="rounded-none max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-base">New strategic action</DialogTitle></DialogHeader>
        <div className="space-y-3 text-xs">
          <Input data-testid="na-activity" placeholder="Action *" value={f.activity} onChange={set("activity")} className="rounded-none text-xs" />
          <Textarea data-testid="na-rationale" placeholder="Strategic rationale" value={f.strategic_rationale} onChange={set("strategic_rationale")} className="rounded-none text-xs" rows={2} />
          <Input data-testid="na-deliverable" placeholder="Deliverable" value={f.deliverable} onChange={set("deliverable")} className="rounded-none text-xs" />
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="na-priority" value={f.priority} onChange={set("priority")} className="border border-slate-200 px-2 py-2 text-xs">
              {["critical", "high", "medium", "low"].map((p) => <option key={p}>{p}</option>)}</select>
            <select data-testid="na-owner" value={f.owner} onChange={set("owner")} className="border border-slate-200 px-2 py-2 text-xs">
              {["Consultant", "Client"].map((p) => <option key={p}>{p}</option>)}</select>
            <Input data-testid="na-duration" type="number" min="0.25" step="0.25" value={f.duration_months} onChange={set("duration_months")} className="rounded-none text-xs" placeholder="Duration (months)" />
            <Input data-testid="na-target" type="date" value={f.target_date} onChange={set("target_date")} className="rounded-none text-xs" />
          </div>
          <Button data-testid="na-submit" disabled={!f.activity.trim()} onClick={submit} className="w-full bg-slate-900 hover:bg-slate-700 text-white rounded-none text-xs">Add to register</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DependencyMap = ({ data }) => {
  const nodes = data.dependency_graph?.nodes || [];
  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const chain = (data.critical_path || []).map((id) => byId[id]).filter(Boolean);
  if (!chain.length) return null;
  return (
    <div className="border border-slate-200 bg-white p-4 mb-4" data-testid="dependency-map">
      <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-3">Critical dependency chain — each step unlocks the next</p>
      <div className="flex flex-col items-start gap-0.5">
        {chain.map((n, i) => (
          <div key={n.id} className="flex flex-col items-start" data-testid={`dep-node-${n.id}`}>
            <div className={`flex items-center gap-2 px-3 py-1.5 border text-[11px] ${ACTION_STATUS_META[n.status]?.cls?.includes("emerald") ? "border-emerald-600 bg-emerald-50" : "border-slate-300 bg-slate-50"}`}>
              <span className="font-bold text-slate-800">{n.activity}</span>
              <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase ${ACTION_STATUS_META[n.status]?.cls || "bg-slate-200"}`}>
                {ACTION_STATUS_META[n.status]?.label || n.status}</span>
            </div>
            {i < chain.length - 1 && <ArrowDown size={14} className="text-slate-400 ml-4 my-0.5" />}
          </div>
        ))}
      </div>
      <p className="text-[10px] text-slate-400 mt-3">{data.dependency_graph.edges.length} dependencies across {nodes.length} actions · {data.dependency_graph.levels} execution levels</p>
    </div>
  );
};

export const CaseActions = ({ caseId }) => {
  const { data, error, reload } = useRegister(caseId);
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  const s = data.summary || {};
  const sc = s.status_counts || {};
  return (
    <div data-testid="case-actions" className="mt-4">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-6" data-testid="actions-summary">
          {[["total", "Actions"], ["phases", "Phases"], ["completed", "Completed"], ["approval_pending", "Awaiting approval"], ["estimated_total_months", "Est. months (dependency-aware)"]].map(([k, l]) => (
            <div key={k}><p className="font-heading text-2xl font-bold text-slate-900">{s[k] ?? 0}</p><p className="text-[10px] uppercase tracking-wide text-slate-400">{l}</p></div>
          ))}
          <div><p className="font-heading text-2xl font-bold text-emerald-700">{s.progress_pct ?? 0}%</p><p className="text-[10px] uppercase tracking-wide text-slate-400">Progress</p></div>
        </div>
        <NewActionDialog caseId={caseId} reload={reload} />
      </div>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="status-legend">
        {Object.entries(ACTION_STATUS_META).map(([k, m]) => (
          <span key={k} className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${m.cls}`}>{m.label} · {sc[k] ?? 0}</span>
        ))}
      </div>
      <DependencyMap data={data} />
      <div className="space-y-4">
        {!data.phases?.length && (
          <p className="text-xs text-slate-400 border border-dashed border-slate-300 bg-white px-4 py-8 text-center" data-testid="actions-empty">
            Action register not yet developed for this case.</p>
        )}
        {(data.phases || []).map((p) => (
          <div key={p.phase} className="border border-slate-200 bg-white" data-testid={`phase-${p.phase}`}>
            <div className="bg-slate-900 text-white px-4 py-2.5 flex items-center justify-between">
              <p className="text-xs font-bold">{p.phase === 99 ? p.phase_name : `Phase ${p.phase} — ${p.phase_name}`}</p>
              <p className="text-[10px] text-white/50 uppercase">M{p.start_month}–M{p.end_month}</p>
            </div>
            <div className="divide-y divide-slate-100">
              {p.activities.map((a) => <ActionRow key={a.id} a={a} caseId={caseId} reload={reload} />)}
            </div>
          </div>
        ))}
      </div>
      <IntegrityNote text={data.loop_note} />
    </div>
  );
};

export const CaseTimeline = ({ caseId }) => {
  const { data, error } = useRegister(caseId);
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  const total = data.total_months || 1;
  const ticks = [];
  for (let m = 0; m <= total; m += 6) ticks.push(m);
  let lastPhase = null;
  return (
    <div data-testid="case-timeline" className="mt-4">
      <p className="text-xs text-slate-500 mb-1">Dependency-aware Gantt · estimated {total} months end-to-end · amber border = critical path</p>
      <div className="relative border border-slate-200 bg-white p-4 overflow-x-auto">
        <div className="min-w-[640px]">
          <div className="flex ml-56 mb-2 relative h-4">
            {ticks.map((m) => (
              <span key={m} className="absolute text-[9px] text-slate-400" style={{ left: `${(m / total) * 100}%` }}>M{m}</span>
            ))}
          </div>
          <div className="space-y-1 relative">
            {(data.gantt || []).map((g) => {
              const phaseHeader = g.phase !== lastPhase; lastPhase = g.phase;
              return (
                <div key={g.id}>
                  {phaseHeader && <p className="text-[9px] uppercase tracking-widest text-slate-400 mt-3 mb-1">{g.phase === 99 ? g.phase_name : `Phase ${g.phase} · ${g.phase_name}`}</p>}
                  <div className="flex items-center gap-3" data-testid={`gantt-row-${g.id}`}>
                    <div className="w-56 shrink-0 text-right">
                      <p className="text-[10px] font-bold text-slate-800 leading-tight truncate" title={g.activity}>{g.activity}</p>
                      <p className="text-[8px] text-slate-400">M{g.start_month}–M{g.end_month}{g.target_date ? ` · due ${g.target_date}` : ""}</p>
                    </div>
                    <div className="flex-1 h-5 bg-slate-50 relative">
                      <div className={`absolute top-0 h-full flex items-center px-1 ${ACTION_STATUS_META[g.status]?.cls || "bg-slate-300"} ${g.critical ? "ring-2 ring-amber-500 ring-inset" : ""}`}
                        style={{ left: `${(g.start_month / total) * 100}%`, width: `${Math.max(((g.end_month - g.start_month) / total) * 100, 1.5)}%` }}
                        title={`${g.activity} · ${g.status}`} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <IntegrityNote text="Start months derive from declared dependencies (an action starts when everything it depends on ends); independent workstreams run in parallel. The critical path is the longest dependency chain." />
    </div>
  );
};
